import React, {
  createContext,
  FC,
  useCallback,
  useState,
  useMemo,
} from 'react';
import { z } from 'zod';
import { useApi, useRequiredContext } from '../hooks';
import { useAppInit } from './appInitContext';
import { useOrderContext } from './orderContext';

import {
  zCarrierSelected,
  zPickupContactInfo,
  tPickupContactInfo,
  zContactInfo,
  tContactInfo,
  summaryTransformer,
  tReturnResponse,
  zUpdateAndAddToReturnArgs,
  tUpdateAndAddToReturnArgs,
  tCarrierSelected,
  tSummaryResponse,
  tReturnPostObject,
  returnTransformer,
  zReturnResponse,
  zReturnInstruction,
  zImage,
  tImage,
  zOrderLine,
} from './zod';

const zReturnContext = z.object({
  setShowSpecificReturn: z
    .function()
    .args(z.string().optional())
    .returns(z.void()),
  showSpecificReturn: z.string().optional(),
  addToReturns: z
    .function()
    .args(zUpdateAndAddToReturnArgs)
    .args(z.boolean().optional())
    .returns(z.void()),
  returns: z.record(z.string(), zUpdateAndAddToReturnArgs),
  removeFromReturns: z
    .function()
    .args(z.string(), z.string())
    .returns(z.void()),
  updateToReturns: z
    .function()
    .args(zUpdateAndAddToReturnArgs)
    .returns(z.void()),
  selectedCarrier: zCarrierSelected.optional(),
  setSelectedCarrier: z.function().args(zCarrierSelected).returns(z.void()),
  postReturn: z.function()
    .args(zPickupContactInfo)
    .args(zContactInfo)
    .args(zImage.array())
    .returns(zReturnResponse),
  validateFile: z.function().args(z.instanceof(File)).returns(z.promise(zOrderLine.array())),
  setPickupContactInfo: z.function().args(zPickupContactInfo).returns(z.void()),
  pickupContactInfo: zPickupContactInfo,
  setShippingContact: z.function().args(zContactInfo).returns(z.void()),
  shippingContactInfo: zContactInfo,
  loading: z.boolean(),
  returnResponse: zReturnResponse,
  instructions: zReturnInstruction.array(),
  setReturns: z.function().args().returns(z.void()),
  setShippingContactInfo: z.function().args().returns(z.void()),
});

type tReturnContext = z.infer<typeof zReturnContext>;

const ReturnContext = createContext<tReturnContext | undefined>(undefined);

export interface IreturnObject {
  [key: string]: tUpdateAndAddToReturnArgs;
}

export const ReturnProvider: FC = ({ children }) => {
  const [showSpecificReturn, setShowSpecificReturn] = useState(undefined);
  const [showTotalNumberOfItems, setShowTotalNumberOfItems] = useState<boolean | undefined>(
    undefined,
  );
  const [returns, setReturns] = useState<IreturnObject>({});
  const [selectedCarrier, setSelectedCarrier] = useState<
    tCarrierSelected | undefined
  >(undefined);
  const [pickupContactInfo, setPickupContactInfo] = useState<tPickupContactInfo | undefined>(
    undefined,
  );
    const [shippingContactInfo, setShippingContactInfo] = useState<tContactInfo | undefined>(
    undefined,
  );

  const [returnResponse, setReturnResponse] = useState<
    tReturnResponse | undefined
  >(undefined);
  const [loading, setLoading] = useState(false);
  const api = useApi();
  // const { getAppTranslation } = useTranslation();
  const { defaultCustomerNumber } = useOrderContext();
  const [instructions, setInstructions] = useState([]);

  const addToReturns = useCallback(
    (props: tUpdateAndAddToReturnArgs, importedFromFile? : boolean) => {
      const { uid, returnData } = props;
      const uniqueReturnId = `${uid}-${returnData.reason}`;
      setReturns((current) => {
        const newCurrent = current;
        if (current[uniqueReturnId]) {
          newCurrent[uniqueReturnId].returnData.amount =
            current[uniqueReturnId].returnData.amount + returnData.amount;
        } else {
          newCurrent[uniqueReturnId] = props;
        }

        return { ...newCurrent };
      });

      setShowTotalNumberOfItems(importedFromFile);
      setShowSpecificReturn(uniqueReturnId);
    },
    [],
  );

  const removeFromReturns = useCallback((uid: string, reason: string) => {
    const uniqueReturnId = `${uid}-${reason}`;
    setReturns((current) => {
      const newCurrent = current;
      delete newCurrent[uniqueReturnId];
      return { ...newCurrent };
    });
  }, []);

  const updateToReturns = useCallback(
    (props: tUpdateAndAddToReturnArgs) => {
      const { uid, returnData, initialReturnReason } = props;

      const newUniqueReturnId = `${uid}-${returnData.reason}`;
      const oldUniqueReturnid = `${uid}-${initialReturnReason}`;

      if (newUniqueReturnId === oldUniqueReturnid) {
        setReturns((current) => {
          const newCurrent = current;
          newCurrent[newUniqueReturnId].returnData.amount = returnData.amount;
          newCurrent[newUniqueReturnId].returnData.returnDescription =
            returnData.returnDescription;
          newCurrent[newUniqueReturnId].returnData.images = returnData.images;
          return { ...newCurrent };
        });
      }

      if (
        newUniqueReturnId !== oldUniqueReturnid &&
        returns[oldUniqueReturnid] &&
        returns[newUniqueReturnId]
      ) {
        setReturns((current) => {
          const newCurrent = { ...current };

          newCurrent[newUniqueReturnId].returnData.amount =
            props.returnData.amount +
            returns[newUniqueReturnId].returnData.amount;

          newCurrent[
            newUniqueReturnId
          ].returnData.returnDescription = `${props.returnData.returnDescription}
        ${returns[newUniqueReturnId].returnData.returnDescription}`;

          newCurrent[newUniqueReturnId].returnData.reason =
            props.returnData.reason;

          newCurrent[newUniqueReturnId].returnData.images = props.returnData.images;

          delete newCurrent[oldUniqueReturnid];
          return { ...newCurrent };
        });
      }

      if (
        newUniqueReturnId !== oldUniqueReturnid &&
        !returns[newUniqueReturnId]
      ) {
        setReturns((current) => {
          const newCurrent = current;
          newCurrent[newUniqueReturnId] = props;
          delete newCurrent[oldUniqueReturnid];
          return { ...newCurrent };
        });
      }
    },
    [returns],
  );

  const sendSummaryApiCall = useCallback(async () => {
    const summaryBodyData = summaryTransformer({
      returns: Object.values(returns),
      carrier: selectedCarrier,
    });

    const summaryApiResponse = await api(
      'summary',
      'POST',
      'no-cors',
      summaryBodyData,
    );

    return summaryApiResponse.data;
  }, [api, returns, selectedCarrier]);

  const sendUploadImageApiCall = useCallback(
    async (file: tImage) => {
      const uploadImageApiResponse = await api(
        'file',
        'POST',
        'no-cors',
        {
          file,
        },
      );

      return uploadImageApiResponse.data;
    }, [api],
  );

  const sendDeleteImageApiCall = useCallback(
    async (reference: string) => {
      const deleteImageApiResponse = await api(
        'file',
        'DELETE',
        'no-cors',
        {
          reference,
        },
      );

      return deleteImageApiResponse.data;
    }, [api],
  );

  const { params } = useAppInit();
  const sendReturnsApiCall = useCallback(
    async (
      summaryResponseData: tSummaryResponse,
      pickupContactData: tPickupContactInfo,
      createShippingContact: boolean,
      shippingContactData: tContactInfo | undefined | null,
    ) => {
      const images = Object.values(returns).reduce((acc, item) => {
        if (item.returnData.images) {
          return [...acc, ...item.returnData.images];
        }
        return acc;
      }, []);

      const returnPostObj: tReturnPostObject = returnTransformer(
        params.languageCode,
        summaryResponseData,
        pickupContactData,
        createShippingContact,
        shippingContactData,
        selectedCarrier.numberOfParcels,
        images,
        defaultCustomerNumber,
      );

      const returnsResponseApi = await api(
        'returns',
        'POST',
        'no-cors',
        returnPostObj,
      );

      if (returnsResponseApi.status === 'Success') {
        setInstructions(returnsResponseApi.data.returnInstructions);
      }
      setReturnResponse(returnsResponseApi);
      return returnsResponseApi;
    },
    [api, selectedCarrier?.numberOfParcels, defaultCustomerNumber, params],
  );

  const postReturn = useCallback(
    async (
      pickupContactData: tPickupContactInfo,
      createShippingContact = false,
      shippingContactData: tContactInfo | null = null) => {
      try {
        setLoading(true);
        const summary: tSummaryResponse = await sendSummaryApiCall();
        const result = await sendReturnsApiCall(
          summary,
          pickupContactData,
          createShippingContact,
          shippingContactData);
        return result;
      } finally {
        setLoading(false);
      }
    },
    [sendReturnsApiCall, sendSummaryApiCall],
  );

  const uploadImage = useCallback(
    async (image: tImage) => {
      const result = await sendUploadImageApiCall(image);
      return result;
    },
    [sendUploadImageApiCall],
  );

  const deleteImage = useCallback(
    async (reference: string) => {
      const result = await sendDeleteImageApiCall(reference);
      return result;
    },
    [sendDeleteImageApiCall],
  );

  const validateFile = useCallback(
    async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('customerNo', params.customerNumber ?? '');
      formData.append('email', params.email ?? '');
      formData.append('languageCode', params.languageCode ?? '');
      const response = await api('returnbyfile/validate', 'POST', 'no-cors', formData, 'application/x-www-form-urlencoded');
      return response;
    },
    [api, params],
  );

  const currentState = useMemo(
    () => ({
      showSpecificReturn,
      setShowSpecificReturn,
      showTotalNumberOfItems,
      setShowTotalNumberOfItems,
      addToReturns,
      returns,
      removeFromReturns,
      updateToReturns,
      setSelectedCarrier,
      selectedCarrier,
      setPickupContactInfo,
      pickupContactInfo,
      setShippingContactInfo,
      shippingContactInfo,
      postReturn,
      uploadImage,
      deleteImage,
      validateFile,
      returnResponse,
      loading,
      instructions,
      setReturns,
    }),
    [
      showSpecificReturn,
      setShowSpecificReturn,
      showTotalNumberOfItems,
      setShowTotalNumberOfItems,
      addToReturns,
      returns,
      removeFromReturns,
      updateToReturns,
      setSelectedCarrier,
      selectedCarrier,
      setPickupContactInfo,
      pickupContactInfo,
      setShippingContactInfo,
      shippingContactInfo,
      postReturn,
      uploadImage,
      deleteImage,
      validateFile,
      returnResponse,
      loading,
      instructions,
      setReturns,
    ],
  );

  return (
    <ReturnContext.Provider value={currentState}>
      {children}
    </ReturnContext.Provider>
  );
};

export const useReturnContext = () => useRequiredContext(ReturnContext);
